import React from 'react'

type Theme = 'light' | 'dark'

interface State {
  theme: Theme
}

interface Context {
  state: State
  dispatch: React.Dispatch<Action>
}

interface ProviderProps {
  children: React.ReactNode
}

type Action = { type: 'SET_THEME'; payload: Theme }

const THEME_STORAGE_KEY = 'deque_theme_preference'
const RESPONDED_KEY = 'deque_theme_has_responded'

const defaultState: State = { theme: 'light' }

const ThemeContext = React.createContext<Context>({
  state: defaultState,
  dispatch: () => {},
})

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
      }
    default:
      throw new Error(`unhandled action type: ${(action as any).type}`)
  }
}

function ThemeProvider(props: ProviderProps) {
  const preferredTheme = getPreferredTheme()
  const [state, dispatch] = React.useReducer(reducer, { theme: preferredTheme })

  return (
    <ThemeContext.Provider value={{ state, dispatch }}>
      {props.children}
    </ThemeContext.Provider>
  )
}

function useTheme(): Context {
  const context = React.useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Helper functions for theme management
function getPreferredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'

  // Check localStorage first
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme
  if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
    return storedTheme
  }

  // Check system preference
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }

  return 'light'
}

function applyThemeToDOM(theme: Theme): void {
  // Apply theme to document
  document.documentElement.setAttribute('data-theme', theme)

  // Apply cauldron theme class to body
  if (theme === 'dark') {
    document.body.classList.remove('cauldron--theme-light')
    document.body.classList.add('cauldron--theme-dark')
  } else {
    document.body.classList.remove('cauldron--theme-dark')
    document.body.classList.add('cauldron--theme-light')
  }
}

function setTheme(dispatch: React.Dispatch<Action>, theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
    dispatch({ type: 'SET_THEME', payload: theme })

    applyThemeToDOM(theme)
  } catch (error) {
    console.error('Failed to set theme:', error)
  }
}

export { ThemeProvider, useTheme, setTheme, getPreferredTheme, applyThemeToDOM }
